'use client';

import React from 'react';
import { RiSparkling2Line } from 'react-icons/ri';
import Image from 'next/image';
import LouiseButton, { CtaTargetType, LouiseButtonColorType } from '@/components/buttons/LouiseButton';
import { IconType } from 'react-icons';
import { useModal } from '@/components/ModalContext/ModalContext';
import ReactHtmlParser from 'react-html-parser';

type Props = {
  title: string;
  subtitle: string;
  badge: string;
  features: Features[];
  locale: string;
};
type Features = {
  icon: IconType;
  title: string;
  description: string;
  videoSrc?: string;
  imgSrc: string;
  button: string;
  metric: string;
  metricLabel: string;
  color: string;
  gradient: boolean;
  ctaTarget: CtaTargetType;
};

const TopThreeBenefits = (props: Props) => {
  const { openModal } = useModal();

  const handleOpenDemoModal = (ctaTarget: string) => {
    switch (ctaTarget) {
      case 'roi-calculator':
        openModal('ROI_CALCULATOR', {});
        return;
      case 'revenue-calculator':
        openModal('REVENUE_CALCULATOR', {});
        return;
      default:
        return;
    }
  };

  const items = props.features.map((feature: Features, index: number) => {
    return {
      icon: feature.icon,
      title: feature.title,
      description: feature.description,
      videoSrc: feature.videoSrc,
      imgSrc: feature.imgSrc,
      button: feature.button,
      metric: feature.metric,
      metricLabel: feature.metricLabel,
      color: feature.color,
      gradient: feature.gradient,
      ctaTarget: feature.ctaTarget,
    };
  });

  return (
    <section className="tw-py-10 lg:tw-py-20 tw-font-['Lexend'] tw-bg-transparent">
      <div className="tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        <div className="tw-text-center tw-mb-16">
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-blue-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
            <RiSparkling2Line className="tw-w-4 tw-h-4" />
            {props.badge}
          </div>
          <h2 className="tw-text-3xl lg:tw-text-4xl xl:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-leading-tight">
            {props.title}
          </h2>
          <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto">{props.subtitle}</p>
        </div>

        <div className="tw-grid grid-cols-1 lg:tw-grid-cols-3 tw-gap-8">
          {items.map((feature, index) => (
            <div
              key={index}
              className="tw-group tw-bg-gradient-to-br tw-from-white tw-to-gray-50/70 tw-rounded-3xl tw-shadow-lg tw-overflow-hidden tw-transition-all tw-duration-300 hover:tw-shadow-2xl hover:tw-scale-[1.02] tw-border tw-border-gray-200/60 tw-flex tw-flex-col tw-backdrop-blur-sm"
            >
              <div className="tw-relative tw-overflow-hidden tw-p-1">
                <div className="tw-rounded-t-2xl tw-overflow-hidden tw-ring-1 tw-ring-gray-200/50 tw-shadow-md">
                  {feature.videoSrc ? (
                    <video
                      src={feature.videoSrc}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="tw-w-full tw-h-48 tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-105"
                      aria-label={feature.title}
                    />
                  ) : (
                    <Image
                      priority={index < 3}
                      quality={100}
                      src={feature.imgSrc}
                      alt={feature.title}
                      width={400}
                      height={250}
                      className="tw-w-full tw-h-48 tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-105"
                    />
                  )}
                </div>

                <div
                  className={`tw-absolute tw-top-8 tw-left-8 tw-w-12 tw-h-12 tw-bg-gradient-to-br tw-from-${feature.color}-400 tw-to-${feature.color}-600 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-ring-2 tw-ring-white/50`}
                >
                  <feature.icon className={`tw-w-6 tw-h-6 tw-text-white`} />
                </div>

                {/* Metric Card */}
                <div className="tw-absolute tw-bottom-8 tw-right-8 tw-bg-white/95 tw-backdrop-blur-md tw-rounded-xl tw-px-4 tw-py-3 tw-shadow-lg tw-ring-1 tw-ring-gray-200/50">
                  <div className="tw-text-lg tw-font-bold tw-text-gray-900">{feature.metric}</div>
                  <div className="tw-text-xs tw-text-gray-600 tw-font-medium">{feature.metricLabel}</div>
                </div>
              </div>

              <div className="tw-p-8 tw-flex tw-flex-col tw-flex-grow">
                <h3 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-leading-tight">{feature.title}</h3>
                <p className="tw-text-gray-600 tw-mb-6 tw-leading-relaxed tw-flex-grow tw-text-sm">
                  {ReactHtmlParser(feature.description)}
                </p>
                <LouiseButton
                  color={feature.color as LouiseButtonColorType}
                  cta={feature.button}
                  ctaTarget={feature.ctaTarget as CtaTargetType}
                  gradiant={feature.gradient}
                  onClick={() => handleOpenDemoModal(feature.ctaTarget)}
                  locale={props.locale}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TopThreeBenefits;
